import moment from 'moment-timezone';
import { endForApi, WALL_CLOCK_FORMAT, wallClockToInstant } from './offerRedeemSlotsValidation';
import {
  OfferOneOffSlotDTO,
  OfferOneOffSlotForm,
  OfferRedeemSlotsDTO,
  OfferRedeemSlotsForm,
  OfferWeeklySlotForm,
} from './type';

export * from './offerRedeemSlotsLabels';
export * from './offerRedeemSlotsValidation';

export const DEFAULT_FRANCHISE_TIMEZONE = 'Europe/Paris';

export const franchiseTimezone = (franchise?: { timezone?: string | null } | null): string =>
  franchise?.timezone || DEFAULT_FRANCHISE_TIMEZONE;

let lastSlotKey = 0;
const nextSlotKey = (prefix: string): string => {
  lastSlotKey += 1;
  return `${prefix}-${lastSlotKey}`;
};

export const newWeeklySlotForm = (): OfferWeeklySlotForm => ({
  key: nextSlotKey('weekly'),
  days: [],
  hours: [{ start: '08:00', end: '18:00' }],
});

export const newOneOffSlotForm = (): OfferOneOffSlotForm => ({ key: nextSlotKey('one-off'), start: null, end: null });

export const newRedeemSlotsForm = (): OfferRedeemSlotsForm => ({ weekly: [newWeeklySlotForm()], oneOff: [] });

const isComplete = (slot: OfferOneOffSlotForm): slot is OfferOneOffSlotForm & OfferOneOffSlotDTO =>
  Boolean(slot.start && slot.end);

export const redeemSlotsFormToDto = (
  form: OfferRedeemSlotsForm | null | undefined,
  timezone: string,
): OfferRedeemSlotsDTO | null => {
  if (!form) return null;
  const weekly = form.weekly.map(group => ({
    days: group.days,
    hours: group.hours.map(hours => ({ start: hours.start, end: endForApi(hours.end) })),
  }));
  // The offset is sent so the server never has to guess the franchise zone.
  const oneOff = form.oneOff.filter(isComplete).map(slot => ({
    start: wallClockToInstant(slot.start, timezone).format(),
    end: wallClockToInstant(slot.end, timezone).format(),
  }));
  return weekly.length === 0 && oneOff.length === 0 ? null : { weekly, oneOff };
};

export const redeemSlotsDtoToForm = (
  dto: OfferRedeemSlotsDTO | null | undefined,
  timezone: string,
): OfferRedeemSlotsForm | null => {
  if (!dto || (dto.weekly.length === 0 && dto.oneOff.length === 0)) return null;
  return {
    weekly: dto.weekly.map(group => ({
      key: nextSlotKey('weekly'),
      days: group.days,
      hours: group.hours.map(hours => ({ start: hours.start, end: hours.end === '24:00' ? '00:00' : hours.end })),
    })),
    oneOff: dto.oneOff.map(slot => ({
      key: nextSlotKey('one-off'),
      start: moment.tz(slot.start, timezone).format(WALL_CLOCK_FORMAT),
      end: moment.tz(slot.end, timezone).format(WALL_CLOCK_FORMAT),
    })),
  };
};

// `undefined` keeps the stored slots (key left out), `null` clears them.
export const redeemSlotsPayload = (
  form: OfferRedeemSlotsForm | null | undefined,
  timezone: string,
): { redeemSlots?: OfferRedeemSlotsDTO | null } =>
  form === undefined ? {} : { redeemSlots: redeemSlotsFormToDto(form, timezone) };
